import { useEffect, useState, type ReactNode } from "react";
import * as D from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "./Button";

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
  full,
  guardClose = true,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** großes Fenster (fast vollflächig) — für umfangreichere Anlage-/Bearbeitungsmasken. */
  full?: boolean;
  /**
   * Default true: Wurde im Fenster etwas eingegeben und es wird per X / Esc / Klick daneben geschlossen,
   * kommt eine Rückfrage ("Verwerfen?"). Ausschalten bei reinen Such-/Bestätigungsfenstern.
   * Die eigenen Buttons des Fensters (Abbrechen/Schließen) schließen weiterhin direkt.
   */
  guardClose?: boolean;
}) {
  const [dirty, setDirty] = useState(false);
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!open) {
      setDirty(false);
      setAsking(false);
    }
  }, [open]);

  const requestClose = (v: boolean) => {
    if (!v && guardClose && dirty) setAsking(true);
    else onOpenChange(v);
  };

  return (
    <>
      <D.Root open={open} onOpenChange={requestClose}>
        <D.Portal>
          <D.Overlay className="fixed inset-0 bg-black/60 animate-fade-in z-40" />
          <D.Content
            onInput={guardClose ? () => setDirty(true) : undefined}
            onChange={guardClose ? () => setDirty(true) : undefined}
            className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100vw-2rem)] ${
              full ? "max-w-5xl" : wide ? "max-w-2xl" : "max-w-md"
            } ${full ? "max-h-[92vh]" : "max-h-[85vh]"} overflow-y-auto card shadow-modal p-5 animate-slide-up`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <D.Title className="text-base font-semibold text-text-primary">
                  {title}
                </D.Title>
                {description && (
                  <D.Description className="text-sm text-text-secondary mt-0.5">
                    {description}
                  </D.Description>
                )}
              </div>
              <D.Close className="text-text-tertiary hover:text-text-primary">
                <X className="w-4 h-4" />
              </D.Close>
            </div>
            {children}
            {footer && (
              <div className="flex justify-end gap-2 mt-5">{footer}</div>
            )}
          </D.Content>
        </D.Portal>
      </D.Root>
      {asking && (
        <Dialog
          open
          onOpenChange={(v) => !v && setAsking(false)}
          guardClose={false}
          title="Eingaben verwerfen?"
          footer={
            <>
              <Button variant="ghost" onClick={() => setAsking(false)}>
                Weiter bearbeiten
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setAsking(false);
                  setDirty(false);
                  onOpenChange(false);
                }}
              >
                Verwerfen
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">
            Deine Eingaben in diesem Fenster sind noch nicht gespeichert. Willst
            du wirklich abbrechen?
          </p>
        </Dialog>
      )}
    </>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel = "Bestätigen",
  danger,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      guardClose={false}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            variant={danger ? "danger" : "primary"}
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-secondary">{message}</p>
    </Dialog>
  );
}
